// Mint an onboarding link to send a publisher.
//
// Sits above the template picker rather than inside it: creating a link and
// finalizing a submission are the two ends of the loop, usually days apart, and
// burying the "start" action inside the "finish" card made it hard to find.
//
// Deliberately dark, against a page of white cards on slate-50. This is the
// one thing on the page that reaches outside CTVBuyer (it produces a URL you
// send to someone else), so it shouldn't read as another form section.

import React from 'react';
import { ClipboardCheck, Copy, Link2, Loader2 } from 'lucide-react';
import { publisherIntakeService } from '../../../services/publisherIntakeService';

const DARK_INPUT =
  'block w-full bg-white/10 border border-white/15 rounded-xl px-4 py-2.5 text-[14px] text-white ' +
  'placeholder:text-slate-400 focus:outline-none focus:border-white/40 focus:ring-0 transition-colors';

interface Props {
  organizations: { id: string; name: string }[];
}

const PublisherIntakeLinkCreator: React.FC<Props> = ({ organizations }) => {
  const [organizationId, setOrganizationId] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [creating, setCreating] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [url, setUrl] = React.useState<string | null>(null);
  const [copied, setCopied] = React.useState(false);

  const create = async () => {
    if (!organizationId) return;
    setCreating(true);
    setError(null);
    try {
      const res = await publisherIntakeService.createLink({
        organizationId,
        contactEmail: email.trim() || undefined,
      });
      setUrl(res.url);
      setCopied(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not create an onboarding link.');
    } finally {
      setCreating(false);
    }
  };

  const copy = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <section
      className="rounded-2xl bg-slate-900 px-5 sm:px-6 py-5 space-y-4"
      style={{
        backgroundImage:
          'radial-gradient(120% 140% at 0% 0%, rgba(56,189,248,0.16), transparent 55%)',
        boxShadow: '0 18px 40px -24px rgba(15,23,42,0.55)',
      }}
    >
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-white/10 text-white shrink-0">
          <Link2 className="w-4 h-4" />
        </div>
        <div className="min-w-0">
          <h3 className="text-[15px] font-semibold tracking-tight text-white">
            Send a publisher an onboarding link
          </h3>
          <p className="text-[12px] text-slate-400 mt-0.5 leading-relaxed">
            They fill in their own channel details, then their submission shows up below.
          </p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        <select
          value={organizationId}
          onChange={(e) => setOrganizationId(e.target.value)}
          className={`${DARK_INPUT} sm:flex-1`}
          aria-label="Organization for the onboarding link"
        >
          <option value="" className="text-slate-900">Select organization…</option>
          {organizations.map((o) => (
            <option key={o.id} value={o.id} className="text-slate-900">{o.name}</option>
          ))}
        </select>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="publisher contact email (optional)"
          className={`${DARK_INPUT} sm:flex-1`}
          aria-label="Publisher contact email"
        />
        <button
          type="button"
          onClick={() => void create()}
          disabled={!organizationId || creating}
          className="inline-flex items-center justify-center gap-1.5 whitespace-nowrap px-5 py-2.5 rounded-xl text-[13px] font-semibold text-slate-900 bg-white hover:bg-slate-100 disabled:opacity-40 disabled:hover:bg-white transition-colors"
        >
          {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
          {creating ? 'Creating…' : 'Create link'}
        </button>
      </div>

      {error && (
        <p className="rounded-xl border border-rose-400/30 bg-rose-500/10 px-4 py-2.5 text-[13px] text-rose-200">
          {error}
        </p>
      )}

      {url && (
        <div className="flex items-center gap-2 rounded-xl border border-emerald-400/30 bg-emerald-500/10 px-3 py-2">
          <code className="flex-1 min-w-0 truncate text-[12px] text-emerald-100">{url}</code>
          <button
            type="button"
            onClick={() => void copy()}
            className="inline-flex items-center gap-1.5 shrink-0 text-[12px] font-semibold text-emerald-200 hover:text-white transition-colors"
          >
            {copied ? <ClipboardCheck className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
      )}
    </section>
  );
};

export default PublisherIntakeLinkCreator;
